import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Play, Star, CheckCircle2, ArrowRight } from "lucide-react";
export function HeroSection() {
  return <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/30 to-background pointer-events-none" />
      <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left">
            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 text-secondary border border-secondary/20 mb-6">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm font-medium">4.8/5 • +800 clientes satisfechos</span>
            </motion.div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-6">
              Tu contador personal por{" "}
              <span className="text-secondary">$350/mes</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
              Declaraciones automáticas • Facturación ilimitada • Asesoría por WhatsApp
            </p>

            {/* Features */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
              {["100% en línea", "Sin filas en el SAT", "Potenciado por IA"].map((feature, i) => <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-secondary" />
                  {feature}
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <Button size="lg" className="btn-primary text-base px-8 w-full sm:w-auto" asChild>
                <Link to="/registro">
                  Comienza gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              {/* TODO: Unhide when video is ready
               <Button
                size="lg"
                variant="outline"
                className="text-base px-8 w-full sm:w-auto group"
                asChild
               >
                <Link to="/demo">
                  <Play className="w-5 h-5 mr-2 group-hover:text-secondary transition-colors" />
                  Ver demo en 2 min
                </Link>
               </Button>
               */}
            </div>
          </motion.div>

          {/* Dashboard Mockup */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="relative">
            <div className="relative animate-float">
              {/* Main Dashboard Card */}
              <div className="glass-card p-6 rounded-3xl">
                <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-6 text-primary-foreground mb-4">
                  <p className="text-sm opacity-80 mb-1">Este mes ahorraste</p>
                  <p className="text-3xl font-bold font-heading">$4,250 MXN</p>
                  <p className="text-sm opacity-80 mt-1">en deducciones detectadas</p>
                </div>
                
                <div className="space-y-3">
                  {[{
                  label: "Declaración mensual",
                  status: "Presentada",
                  color: "bg-secondary"
                }, {
                  label: "Facturas clasificadas",
                  status: "23 de 23",
                  color: "bg-secondary"
                }, {
                  label: "Próximo vencimiento",
                  status: "17 enero",
                  color: "bg-accent"
                }].map((item, i) => <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                      <span className="text-sm text-muted-foreground">{item.label}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.color} text-white`}>
                        {item.status}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Floating AI Chat Card */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.8
            }} className="absolute -bottom-4 -left-4 glass-card p-4 rounded-2xl shadow-lg max-w-[200px]">
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                    <span className="text-secondary-foreground text-xs font-bold">IA</span>
                  </div>
                  <div>
                    <p className="text-xs text-foreground">
                      Detecté 3 deducciones nuevas en tus gastos 🎯
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Stats Card */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 1
            }} className="absolute -top-4 -right-4 glass-card p-4 rounded-2xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Estatus SAT</p>
                    <p className="text-sm font-semibold text-secondary">Al día</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}